import { useAuiState } from '@assistant-ui/react'
import { RefreshCw } from 'lucide-react'
import { useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { AgentThread } from '@/components/agent/AgentThread'
import { ClarificationCard } from '@/components/agent/ClarificationCard'
import { ProposalFindings } from '@/components/schema/agent/ProposalFindings'
import { ProposalEvidence } from '@/components/schema/agent/SchemaAgentReview'
import { Button } from '@/components/ui/button'
import { type AgentActivity, AgentActivityContext } from '@/features/agent/AgentActivityContext'
import { AgentChatHost } from '@/features/agent/AgentChatHost'
import { FreshContextRetryContext } from '@/features/agent/FailedSendRetry'
import type { SchemaSession } from '@/services/api/schemaSessions'
import { useSchemaChatSync } from './hooks/use-schema-chat-sync'
import { isSessionActive, isSessionLocked } from './hooks/use-schema-session'
import type { useWorkbench } from './hooks/use-workbench'
import { ProposalReadyNotice } from './ProposalReviewNavigation'
import { proposalActivityKey } from './proposal-activity'
import {
  SchemaClarificationContext,
  type SchemaClarificationState,
  SchemaClarificationToolUI,
} from './SchemaClarificationToolUI'
import { type SchemaQuestion, SchemaQuestionComposer } from './SchemaConversationContext'

type WorkbenchActions = ReturnType<typeof useWorkbench>

export function WorkbenchConversation({
  session,
  actions,
  question,
  lastTool,
}: {
  session: SchemaSession
  actions: WorkbenchActions
  question: SchemaQuestion | null
  /** The run's most recent tool call, for the live activity row. */
  lastTool: string | null
}) {
  return (
    <>
      {session.pendingProposal ? (
        <ProposalReadyNotice />
      ) : (
        <div className="max-h-[50%] shrink-0 overflow-y-auto">
          <ConversationReview session={session} actions={actions} />
        </div>
      )}
      <div className="min-h-64 flex-1 overflow-hidden">
        {session.agentThreadId && (
          <AgentChatHost
            caseId={session.caseId}
            threadId={session.agentThreadId}
            currentView="schema"
          >
            <SchemaChat
              session={session}
              actions={actions}
              question={question}
              lastTool={lastTool}
            />
          </AgentChatHost>
        )}
      </div>
    </>
  )
}

function ConversationReview({
  session,
  actions,
}: {
  session: SchemaSession
  actions: WorkbenchActions
}) {
  const { t } = useTranslation()
  const failed = session.latestRun?.proposal?.errors.length ? session.latestRun.proposal : null
  const proposal =
    session.pendingProposal ?? failed ?? session.reviewProposal ?? session.latestRun?.proposal
  return (
    <>
      {proposal && (
        <details className="border-b px-4 py-3 text-sm">
          <summary className="cursor-pointer font-medium">
            {t('schemaAgent.workbench.reasoning')}
          </summary>
          <p className="my-3 text-muted-foreground">{proposal.rationale}</p>
          <ProposalEvidence proposal={proposal} />
        </details>
      )}
      <ConversationStatus session={session} actions={actions} />
    </>
  )
}

function ConversationStatus({
  session,
  actions,
}: {
  session: SchemaSession
  actions: WorkbenchActions
}) {
  const { t } = useTranslation()
  const proposal = session.pendingProposal ?? session.latestRun?.proposal
  return (
    <>
      {!!proposal?.errors.length && (
        <div className="space-y-3 border-b p-3">
          <ProposalFindings proposal={proposal} />
          <Button
            size="sm"
            disabled={isSessionLocked(session) || actions.busy || !!session.pendingProposal}
            onClick={() => void actions.repair()}
          >
            <RefreshCw className="size-4" />
            {t('schemaAgent.review.repair')}
          </Button>
        </div>
      )}
      {session.latestRun?.errorMessage && (
        <p role="alert" className="border-b p-3 text-sm text-destructive">
          {session.latestRun.errorMessage}
        </p>
      )}
    </>
  )
}

function SchemaChat({
  session,
  actions,
  question,
  lastTool,
}: {
  session: SchemaSession
  actions: WorkbenchActions
  question: SchemaQuestion | null
  lastTool: string | null
}) {
  const { t } = useTranslation()
  const container = useRef<HTMLDivElement>(null)
  const sync = useSchemaChatSync(session.caseId)
  const activity = useSchemaAgentActivity(session, lastTool, sync.settling)
  const busy = actions.busy
  const clarification: SchemaClarificationState = {
    clarification: session.latestRun?.clarification ?? null,
    waiting: session.latestRun?.status === 'awaiting_input',
    busy,
    answer: async answers => {
      await actions.answer(answers)
    },
  }
  return (
    <SchemaClarificationContext.Provider value={clarification}>
      <SchemaClarificationToolUI />
      <div ref={container} className="flex h-full flex-col">
        <SchemaQuestionComposer question={question} container={container} />
        <UnthreadedClarification state={clarification} />
        {sync.error && (
          <div role="alert" className="flex items-center gap-3 border-b p-3 text-sm">
            <p className="text-destructive">{t('schemaAgent.workbench.reviewLoadError')}</p>
            <Button size="sm" variant="outline" onClick={sync.retry}>
              {t('schemaAgent.error.retry')}
            </Button>
          </div>
        )}
        <AgentActivityContext.Provider value={activity}>
          <div className="min-h-0 flex-1">
            <FreshContextRetryContext.Provider value={true}>
              <AgentThread
                controls={{
                  disabled: isSessionLocked(session) || busy || sync.settling || sync.error,
                  hideTools: true,
                  placeholder: t(composerPlaceholderKey(session)),
                }}
              />
            </FreshContextRetryContext.Provider>
          </div>
        </AgentActivityContext.Provider>
      </div>
    </SchemaClarificationContext.Provider>
  )
}

/** The open stop answered above the thread, only when the transcript lacks its tool call
 * (the run's transcript is archived best-effort); otherwise the thread shows it in place. */
function UnthreadedClarification({ state }: { state: SchemaClarificationState }) {
  const inThread = useAuiState(s =>
    s.thread.messages.some(
      message =>
        message.role === 'assistant' &&
        message.content.some(
          part =>
            part.type === 'tool-call' &&
            part.toolName === 'request_clarification' &&
            part.result === undefined
        )
    )
  )
  if (!state.waiting || !state.clarification || inThread) return null
  return (
    <div className="max-h-[50%] shrink-0 overflow-y-auto border-b p-3">
      <ClarificationCard
        clarification={state.clarification}
        busy={state.busy}
        onSubmit={state.answer}
      />
    </div>
  )
}

function composerPlaceholderKey(session: SchemaSession): string {
  if (session.latestRun?.status === 'awaiting_input')
    return 'schemaAgent.workbench.answerPlaceholder'
  if (session.pendingProposal) return 'schemaAgent.workbench.pendingChatPlaceholder'
  if (isSessionActive(session)) return 'schemaAgent.workbench.workingPlaceholder'
  return 'schemaAgent.workbench.chatPlaceholder'
}

/** What the thread's activity row shows: a server-side run narrating into the
 * chat (assistant-ui cannot see it), or the refresh that settles a finished turn. */
function useSchemaAgentActivity(
  session: SchemaSession,
  lastTool: string | null,
  settling: boolean
): AgentActivity | null {
  const { t } = useTranslation()
  const chatActivity = useAuiState(state =>
    state.thread.isRunning ? proposalActivityKey(state.thread.messages) : null
  )
  if (isSessionActive(session))
    return {
      label: t('schemaAgent.workbench.running'),
      detail: lastTool ? t(`schemaAgent.progress.tool.${lastTool}`, lastTool) : undefined,
      startedAt: session.latestRun?.startedAt,
    }
  if (settling) return { label: t('schemaAgent.workbench.updating') }
  return chatActivity ? { label: t(chatActivity) } : null
}
